import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import PortalDropdown, { DropdownMenuItem } from "views_shared/app/javascript/components/PortalDropdown";
import { mediaOutletUrl, requestUrl } from "views_shared/app/javascript/utils/urls";
import SectionCard from "../../profiles/SectionCard";
import SectionSkeleton from "../../profiles/SectionSkeleton";
import PaginationGroup from "../../profiles/PaginationGroup";
import useMediaPosts from "../data/use_media_posts";

const POSTS_PER_PAGE = 5;
const SEARCH_DELAY_MS = 300;
const SORT_OPTIONS = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
];
const PLACE_PARAMS = { page: "posts_page", sort: "posts_sort", search: "posts_q" };

// The viewer's place in the list lives in the address, so Back from a post returns to it.
function readPlace() {
  const params = new URLSearchParams(globalThis.location?.search || "");
  const page = Number.parseInt(params.get(PLACE_PARAMS.page), 10);
  const sort = params.get(PLACE_PARAMS.sort);
  return {
    page: Number.isInteger(page) && page > 1 ? page : 1,
    sort: SORT_OPTIONS.some((option) => option.value === sort) ? sort : "newest",
    search: (params.get(PLACE_PARAMS.search) || "").trim(),
  };
}

function writePlace({ page, sort, search }) {
  const url = new URL(globalThis.location.href);
  const setParam = (key, value, isDefault) => {
    if (isDefault) {
      url.searchParams.delete(key);
    } else {
      url.searchParams.set(key, value);
    }
  };
  setParam(PLACE_PARAMS.page, page, page === 1);
  setParam(PLACE_PARAMS.sort, sort, sort === "newest");
  setParam(PLACE_PARAMS.search, search, !search);
  if (url.href !== globalThis.location.href) {
    globalThis.history.replaceState(globalThis.history.state, "", url);
  }
}

function MediaPostRow({ post }) {
  const postUrl = requestUrl({ slug: post.slug, migrate_id: post.migrate_id });
  const logo = post.media_outlet_image_url ? (
    <img src={post.media_outlet_image_url} alt="" />
  ) : (
    <i className="fa-regular fa-newspaper" aria-hidden="true" />
  );

  return (
    <li className="media-post d-flex">
      {post.media_outlet_slug ? (
        <a
          className="media-post-logo"
          href={mediaOutletUrl({ slug: post.media_outlet_slug, migrate_id: post.media_outlet_migrate_id })}
          aria-label={post.media_outlet_name}
        >
          {logo}
        </a>
      ) : (
        <div className="media-post-logo">{logo}</div>
      )}
      <div className="media-post-body">
        <a className="media-post-title paragraph-md fw-bold" href={postUrl}>
          {post.title}
        </a>
        {post.details_preview && (
          <p className="media-post-preview paragraph-md text-secondary mb-0">{post.details_preview}</p>
        )}
      </div>
    </li>
  );
}

MediaPostRow.propTypes = {
  post: PropTypes.shape({
    title: PropTypes.string,
    slug: PropTypes.string,
    migrate_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    details_preview: PropTypes.string,
    media_outlet_name: PropTypes.string,
    media_outlet_slug: PropTypes.string,
    media_outlet_migrate_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    media_outlet_image_url: PropTypes.string,
  }).isRequired,
};

/**
 * MediaRequestsSection — the media profile's Posts card (Figma "Posts"): the reporter's requests,
 * five to a page, with a search over titles and details and a newest/oldest sort. Each row shows the
 * outlet's logo, the linked title and a two-line preview.
 *
 * The page, sort and search are kept in the address (replaced, not pushed), so Back from a post lands
 * on the same place, and clearing a search returns to the page the viewer was on before it.
 *
 * A paid feature: the server-side RPC returns [] to viewers who aren't the profile's own user, an
 * admin, or a paid-plan viewer, so this card is rendered only for entitled viewers (unentitled
 * authenticated viewers get MediaRequestsTeaser instead). A reporter with no posts gets no card.
 *
 * @param {Object} props
 * @param {number|string} props.mediaProfileId - The media profile whose posts to show
 */
function MediaRequestsSection({ mediaProfileId }) {
  const [initialPlace] = useState(readPlace);
  const [page, setPage] = useState(initialPlace.page);
  const [sort, setSort] = useState(initialPlace.sort);
  const [query, setQuery] = useState(initialPlace.search);
  const [search, setSearch] = useState(initialPlace.search);
  const placeBeforeSearch = useRef(null);

  useEffect(() => {
    writePlace({ page, sort, search });
  }, [page, sort, search]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = query.trim();
      if (trimmed === search) {
        return;
      }
      if (!search) {
        placeBeforeSearch.current = { page, sort };
        setPage(1);
      } else if (!trimmed) {
        const saved = placeBeforeSearch.current;
        setPage(saved && saved.sort === sort ? saved.page : 1);
        placeBeforeSearch.current = null;
      } else {
        setPage(1);
      }
      setSearch(trimmed);
    }, SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [query, search, page, sort]);

  const { posts, total, isLoading, hasLoaded } = useMediaPosts({
    mediaProfileId,
    page,
    perPage: POSTS_PER_PAGE,
    sort,
    search,
  });

  // A remembered page can be past the end once posts are removed; start over rather than show nothing.
  const pastTheEnd = hasLoaded && !isLoading && posts.length === 0 && page > 1;
  useEffect(() => {
    if (pastTheEnd) {
      setPage(1);
    }
  }, [pastTheEnd]);

  if (!hasLoaded || pastTheEnd) {
    return <SectionSkeleton lines={4} />;
  }
  if (total === 0 && !search && !isLoading) {
    return null;
  }

  const chooseSort = (value) => {
    setSort(value);
    setPage(1);
  };

  return (
    <SectionCard id="media-section-requests" title="Posts">
      <div className="media-posts-toolbar d-flex align-items-center">
        <PortalDropdown
          toggleClassName="btn btn-secondary media-posts-sort"
          toggleStyle={{ boxShadow: "0 1px 2px 0 rgb(16 24 40 / 5%)" }}
          toggleLabel="Sort by"
          toggleIcon="fa-solid fa-chevron-down"
          menuWidth="12rem"
          align="start"
        >
          {SORT_OPTIONS.map((option) => (
            <DropdownMenuItem
              key={option.value}
              icon={sort === option.value ? "fa-solid fa-check" : "fa-solid"}
              label={option.label}
              active={sort === option.value}
              onClick={() => chooseSort(option.value)}
            />
          ))}
        </PortalDropdown>
        <div className="media-posts-search flex-grow-1">
          <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
          <input
            type="search"
            className="form-control"
            placeholder="Search"
            aria-label="Search posts"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
      </div>

      {posts.length === 0 ? (
        !isLoading && <div className="paragraph-md text-secondary">No posts match “{search}”.</div>
      ) : (
        <ul className={`media-posts list-unstyled mb-0${isLoading ? " opacity-50" : ""}`} aria-busy={isLoading}>
          {posts.map((post) => (
            <MediaPostRow key={post.id} post={post} />
          ))}
        </ul>
      )}

      <PaginationGroup
        className="mt-4"
        page={page}
        pageCount={Math.ceil(total / POSTS_PER_PAGE)}
        onPageChange={setPage}
        ariaLabel="Posts pages"
      />
    </SectionCard>
  );
}

MediaRequestsSection.propTypes = {
  mediaProfileId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};

export default MediaRequestsSection;
